<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ResultVisualization from './components/ResultVisualization.vue';
import ReportCollaboratorsDialog from './components/ReportCollaboratorsDialog.vue';
import DataAssistantReportsAPI from 'dashboard/api/dataAssistantReports';
import { openConversationFilter } from './helpers/openConversationFilter';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const reports = useMapGetter('dataAssistantReports/getReports');
const currentReport = useMapGetter('dataAssistantReports/getCurrentReport');
const items = useMapGetter('dataAssistantReports/getItems');
const itemResults = useMapGetter('dataAssistantReports/getItemResults');
const uiFlags = useMapGetter('dataAssistantReports/getUIFlags');
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');
const collaboratorsDialogRef = ref(null);
const openCollaboratorsDialog = () => collaboratorsDialogRef.value?.open();
const hasCollaborators = computed(() => {
  const r = currentReport.value;
  if (!r) return false;
  return (r.user_ids?.length || 0) > 0 || (r.team_ids?.length || 0) > 0;
});

const selectedReportId = ref(null);
const showNewReportForm = ref(false);
const newReportName = ref('');
const deleteReportDialogRef = ref(null);
const pendingDeleteReportId = ref(null);
const deleteItemDialogRef = ref(null);
const pendingDeleteItemId = ref(null);

// Inline editing state for report rename
const editingReportId = ref(null);
const editingReportName = ref('');

// Inline editing state for item rename
const editingItemId = ref(null);
const editingItemTitle = ref('');

const REFRESH_OPTIONS = [
  { value: null, label: 'AUTO_REFRESH_NONE' },
  { value: 30, label: 'AUTO_REFRESH_30S' },
  { value: 60, label: 'AUTO_REFRESH_1M' },
  { value: 300, label: 'AUTO_REFRESH_5M' },
  { value: 600, label: 'AUTO_REFRESH_10M' },
];

// Per-item interval timers: itemId -> intervalId
const itemTimers = new Map();
// Local v-model state for each item's selected interval: itemId -> seconds|null
const itemIntervals = ref({});
// Which item's interval dropdown is open (null = none)
const openDropdownItemId = ref(null);

const LS_KEY = 'da_report_item_intervals';
const loadIntervalsFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    return {};
  }
};
const saveIntervalToStorage = (itemId, seconds) => {
  const stored = loadIntervalsFromStorage();
  if (seconds) {
    stored[String(itemId)] = seconds;
  } else {
    delete stored[String(itemId)];
  }
  localStorage.setItem(LS_KEY, JSON.stringify(stored));
};

const clearItemTimer = itemId => {
  if (itemTimers.has(itemId)) {
    clearInterval(itemTimers.get(itemId));
    itemTimers.delete(itemId);
  }
};

const clearAllTimers = () => {
  itemTimers.forEach((_, id) => clearItemTimer(id));
};

const startItemTimer = (itemId, seconds) => {
  clearItemTimer(itemId);
  if (!seconds) return;
  const timerId = setInterval(() => {
    store.dispatch('dataAssistantReports/runItem', {
      reportId: selectedReportId.value,
      itemId,
      forceRefresh: true,
    });
  }, seconds * 1000);
  itemTimers.set(itemId, timerId);
};

// Initialize local interval state from localStorage (called after fetchReport)
const initItemIntervals = loadedItems => {
  clearAllTimers();
  const stored = loadIntervalsFromStorage();
  const intervals = {};
  loadedItems.forEach(item => {
    const seconds = stored[String(item.id)] ?? null;
    intervals[item.id] = seconds;
    if (seconds) startItemTimer(item.id, seconds);
  });
  itemIntervals.value = intervals;
};

onBeforeUnmount(() => clearAllTimers());

const navigateToReport = id => {
  const accountId = route.params.accountId;
  if (id) {
    router.replace({
      name: 'data_assistant_report_show',
      params: { accountId, reportId: id },
    });
  } else {
    router.replace({
      name: 'data_assistant_reports_index',
      params: { accountId },
    });
  }
};

const selectReport = async id => {
  clearAllTimers();
  selectedReportId.value = id;
  navigateToReport(id);
  await store.dispatch('dataAssistantReports/fetchReport', id);
  initItemIntervals(items.value);
};

onMounted(async () => {
  await store.dispatch('dataAssistantReports/fetchReports');
  const paramId = Number(route.params.reportId);
  if (paramId) {
    await selectReport(paramId);
  } else if (reports.value.length) {
    await selectReport(reports.value[0].id);
  }
});

watch(
  () => route.params.reportId,
  newId => {
    if (newId && Number(newId) !== selectedReportId.value) {
      selectReport(Number(newId));
    }
  }
);

const createReport = async () => {
  const name = newReportName.value.trim();
  if (!name) return;
  const report = await store.dispatch('dataAssistantReports/createReport', {
    name,
  });
  newReportName.value = '';
  showNewReportForm.value = false;
  await selectReport(report.id);
};

const confirmDeleteReport = id => {
  pendingDeleteReportId.value = id;
  deleteReportDialogRef.value?.open();
};

const deleteReport = async () => {
  const id = pendingDeleteReportId.value;
  deleteReportDialogRef.value?.close();
  await store.dispatch('dataAssistantReports/deleteReport', id);
  if (selectedReportId.value === id) {
    clearAllTimers();
    selectedReportId.value = null;
    navigateToReport(null);
  }
};

const confirmRemoveItem = itemId => {
  pendingDeleteItemId.value = itemId;
  deleteItemDialogRef.value?.open();
};

const removeItem = async () => {
  const itemId = pendingDeleteItemId.value;
  deleteItemDialogRef.value?.close();
  clearItemTimer(itemId);
  saveIntervalToStorage(itemId, null);
  const { [itemId]: _, ...rest } = itemIntervals.value;
  itemIntervals.value = rest;
  await store.dispatch('dataAssistantReports/removeItem', {
    reportId: selectedReportId.value,
    itemId,
  });
};

const refreshItem = itemId => {
  store.dispatch('dataAssistantReports/runItem', {
    reportId: selectedReportId.value,
    itemId,
  });
};

const onItemRefreshIntervalChange = (itemId, newInterval) => {
  saveIntervalToStorage(itemId, newInterval);
  startItemTimer(itemId, newInterval);
};

const toggleItemDropdown = itemId => {
  openDropdownItemId.value =
    openDropdownItemId.value === itemId ? null : itemId;
};

const closeDropdown = () => {
  openDropdownItemId.value = null;
};

const selectRefreshInterval = (itemId, value) => {
  closeDropdown();
  itemIntervals.value = { ...itemIntervals.value, [itemId]: value };
  onItemRefreshIntervalChange(itemId, value);
};

// Report rename
let reportEditReady = false;
const startEditingReport = async (report, event) => {
  event?.stopPropagation();
  reportEditReady = false;
  editingReportId.value = report.id;
  editingReportName.value = report.name;
  await nextTick();
  reportEditReady = true;
};

const saveReportName = async () => {
  if (!reportEditReady) return;
  const name = editingReportName.value.trim();
  const id = editingReportId.value;
  editingReportId.value = null;
  if (!name || !id) return;
  await store.dispatch('dataAssistantReports/updateReport', { id, name });
};

const cancelEditingReport = () => {
  editingReportId.value = null;
};

// Item rename
let itemEditReady = false;
const startEditingItem = async item => {
  itemEditReady = false;
  editingItemId.value = item.id;
  editingItemTitle.value = item.title;
  await nextTick();
  itemEditReady = true;
};

const saveItemTitle = async () => {
  if (!itemEditReady) return;
  const title = editingItemTitle.value.trim();
  const itemId = editingItemId.value;
  editingItemId.value = null;
  if (!title || !itemId) return;
  await store.dispatch('dataAssistantReports/updateItem', {
    reportId: selectedReportId.value,
    itemId,
    title,
  });
};

const cancelEditingItem = () => {
  editingItemId.value = null;
};

// Report reorder
const moveReport = async (index, direction) => {
  const report = reports.value[index];
  const dir = direction === -1 ? 'up' : 'down';
  await store.dispatch('dataAssistantReports/moveReport', {
    id: report.id,
    direction: dir,
  });
};

// Item reorder
const moveItem = async (index, direction) => {
  const item = items.value[index];
  const dir = direction === -1 ? 'up' : 'down';
  await store.dispatch('dataAssistantReports/moveItem', {
    reportId: selectedReportId.value,
    itemId: item.id,
    direction: dir,
  });
};

const openConversationFilterHandler = (item, payload) => {
  openConversationFilter(router, 'dari', item, payload);
};

const exportCsv = async item => {
  const { data } = await DataAssistantReportsAPI.downloadItemCsv(
    selectedReportId.value,
    item.id
  );
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${item.title || 'export'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div class="flex h-full w-full overflow-hidden bg-n-background">
    <!-- Sidebar: report list -->
    <div
      class="flex w-72 shrink-0 flex-col border-r border-n-weak bg-n-solid-2"
    >
      <div
        class="flex items-center justify-between border-b border-n-weak px-4 py-3"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('DATA_ASSISTANT.REPORTS') }}
        </h2>
        <button
          v-if="isAdmin"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          @click="showNewReportForm = !showNewReportForm"
        >
          + {{ t('DATA_ASSISTANT.NEW_REPORT') }}
        </button>
      </div>

      <!-- New report form -->
      <div
        v-if="isAdmin && showNewReportForm"
        class="border-b border-n-weak px-4 py-3"
      >
        <input
          v-model="newReportName"
          class="mb-2 w-full rounded border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="t('DATA_ASSISTANT.REPORT_NAME')"
          @keydown.enter="createReport"
        />
        <button
          class="w-full rounded bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          :disabled="!newReportName.trim() || uiFlags.isCreating"
          @click="createReport"
        >
          {{ t('DATA_ASSISTANT.NEW_REPORT') }}
        </button>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-if="uiFlags.isFetching" class="p-4 text-sm text-n-slate-11">
          {{ t('DATA_ASSISTANT.LOADING') }}
        </div>
        <template v-else>
          <div
            v-for="(report, rIndex) in reports"
            :key="report.id"
            class="group flex cursor-pointer items-center border-b border-n-weak px-4 py-3 hover:bg-n-alpha-2"
            :class="{
              'bg-n-alpha-2': selectedReportId === report.id,
            }"
            @click="selectReport(report.id)"
          >
            <!-- Move up/down -->
            <div
              v-if="isAdmin"
              class="mr-2 flex shrink-0 flex-col gap-0.5 opacity-0 group-hover:opacity-100"
            >
              <button
                v-if="rIndex > 0"
                class="rounded p-0.5 text-n-slate-10 hover:text-n-slate-12"
                :title="t('DATA_ASSISTANT.MOVE_UP')"
                @click.stop="moveReport(rIndex, -1)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="h-3 w-3"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <button
                v-if="rIndex < reports.length - 1"
                class="rounded p-0.5 text-n-slate-10 hover:text-n-slate-12"
                :title="t('DATA_ASSISTANT.MOVE_DOWN')"
                @click.stop="moveReport(rIndex, 1)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="h-3 w-3"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              {{ report.name }}
            </span>
            <button
              v-if="isAdmin"
              class="ml-2 shrink-0 text-xs text-n-slate-10 hover:text-n-ruby-11"
              @click.stop="confirmDeleteReport(report.id)"
            >
              {{ t('DATA_ASSISTANT.DELETE') }}
            </button>
          </div>
          <div v-if="!reports.length" class="p-4 text-sm text-n-slate-11">
            {{ t('DATA_ASSISTANT.NO_REPORTS') }}
          </div>
        </template>
      </div>
    </div>

    <!-- Main: report dashboard -->
    <div class="flex min-w-0 flex-1 flex-col">
      <template v-if="selectedReportId && currentReport">
        <div
          class="flex items-center justify-between gap-4 border-b border-n-weak px-6 py-4"
        >
          <div class="min-w-0 flex-1">
            <input
              v-if="editingReportId === currentReport.id"
              v-model="editingReportName"
              class="w-full rounded border border-n-brand bg-n-solid-1 px-2 py-1 text-xl font-semibold text-n-slate-12 outline-none"
              @keydown.enter="saveReportName"
              @keydown.escape="cancelEditingReport"
              @blur="saveReportName"
              @vue:mounted="({ el }) => el.focus()"
            />
            <div
              v-else
              class="group flex items-center gap-2"
              @dblclick="isAdmin && startEditingReport(currentReport, $event)"
            >
              <h1
                class="truncate text-xl font-semibold text-n-slate-12"
                :class="{ 'cursor-pointer': isAdmin }"
              >
                {{ currentReport.name }}
              </h1>
              <button
                v-if="isAdmin"
                class="shrink-0 rounded p-1 text-n-slate-10 opacity-0 hover:text-n-slate-12 group-hover:opacity-100"
                :title="t('DATA_ASSISTANT.RENAME_REPORT')"
                @click="startEditingReport(currentReport, $event)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="h-3.5 w-3.5"
                >
                  <path
                    d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            v-if="isAdmin"
            class="shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium"
            :class="
              hasCollaborators
                ? 'border-n-teal-7 bg-n-teal-3 text-n-teal-12 hover:bg-n-teal-4'
                : 'border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-2'
            "
            @click="openCollaboratorsDialog"
          >
            {{ t('DATA_ASSISTANT.COLLABORATORS.BUTTON') }}
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="!items.length" class="text-sm text-n-slate-11">
            {{ t('DATA_ASSISTANT.NO_REPORTS') }}
          </div>

          <div class="flex flex-col gap-4">
            <div
              v-for="(item, index) in items"
              :key="item.id"
              class="flex items-stretch gap-2"
            >
              <!-- Move up/down buttons -->
              <div
                v-if="isAdmin"
                class="flex shrink-0 flex-col justify-center gap-1"
              >
                <button
                  v-if="index > 0"
                  class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :title="t('DATA_ASSISTANT.MOVE_UP')"
                  @click="moveItem(index, -1)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-3.5 w-3.5"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  v-if="index < items.length - 1"
                  class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :title="t('DATA_ASSISTANT.MOVE_DOWN')"
                  @click="moveItem(index, 1)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-3.5 w-3.5"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <!-- Item card -->
              <div
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="mb-3 flex items-start justify-between gap-2">
                  <input
                    v-if="editingItemId === item.id"
                    v-model="editingItemTitle"
                    class="min-w-0 flex-1 rounded border border-n-brand bg-n-solid-1 px-2 py-0.5 text-sm font-semibold text-n-slate-12 outline-none"
                    @keydown.enter="saveItemTitle"
                    @keydown.escape="cancelEditingItem"
                    @blur="saveItemTitle"
                    @vue:mounted="({ el }) => el.focus()"
                  />
                  <div
                    v-else
                    class="group/title flex min-w-0 flex-1 items-center gap-1"
                    @dblclick="isAdmin && startEditingItem(item)"
                  >
                    <h3
                      class="text-sm font-semibold text-n-slate-12"
                      :class="{ 'cursor-pointer': isAdmin }"
                      :title="t('DATA_ASSISTANT.RENAME_ITEM')"
                    >
                      {{ item.title }}
                    </h3>
                    <button
                      v-if="isAdmin"
                      class="shrink-0 rounded p-0.5 text-n-slate-10 opacity-0 hover:text-n-slate-12 group-hover/title:opacity-100"
                      :title="t('DATA_ASSISTANT.RENAME_ITEM')"
                      @click="startEditingItem(item)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-3 w-3"
                      >
                        <path
                          d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"
                        />
                      </svg>
                    </button>
                  </div>
                  <div class="flex shrink-0 items-center gap-1">
                    <!-- Refresh + interval split button -->
                    <div class="relative flex items-center">
                      <div
                        class="rounded-lg shadow outline-1 outline"
                        :class="
                          itemIntervals[item.id]
                            ? 'outline-transparent'
                            : 'outline-n-container'
                        "
                      >
                        <Button
                          icon="i-lucide-refresh-cw"
                          size="sm"
                          :color="itemIntervals[item.id] ? 'blue' : 'slate'"
                          class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
                          :title="t('DATA_ASSISTANT.REFRESH')"
                          @click="refreshItem(item.id)"
                        />
                        <Button
                          icon="i-lucide-chevron-down"
                          size="sm"
                          :color="itemIntervals[item.id] ? 'blue' : 'slate'"
                          class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
                          :title="t('DATA_ASSISTANT.AUTO_REFRESH')"
                          @click="toggleItemDropdown(item.id)"
                        />
                      </div>
                      <div
                        v-if="openDropdownItemId === item.id"
                        v-on-clickaway="closeDropdown"
                        class="dropdown-pane dropdown-pane--open left-auto top-full mt-0.5 ltr:right-0 rtl:left-0 max-w-[12.5rem] min-w-[9.75rem]"
                      >
                        <WootDropdownMenu class="mb-0">
                          <WootDropdownItem
                            v-for="option in REFRESH_OPTIONS"
                            :key="String(option.value)"
                          >
                            <Button
                              :label="t(`DATA_ASSISTANT.${option.label}`)"
                              :icon="
                                itemIntervals[item.id] === option.value
                                  ? 'i-lucide-check'
                                  : ''
                              "
                              ghost
                              slate
                              sm
                              start
                              class="w-full"
                              @click="
                                selectRefreshInterval(item.id, option.value)
                              "
                            />
                          </WootDropdownItem>
                        </WootDropdownMenu>
                      </div>
                    </div>

                    <button
                      v-if="itemResults[item.id]?.result_data"
                      class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :title="t('DATA_ASSISTANT.EXPORT_CSV')"
                      @click="exportCsv(item)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-3.5 w-3.5"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 3a.75.75 0 01.75.75v7.69l2.72-2.72a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 111.06-1.06l2.72 2.72V3.75A.75.75 0 0110 3zM3.75 15a.75.75 0 000 1.5h12.5a.75.75 0 000-1.5H3.75z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>

                    <!-- Remove item -->
                    <button
                      v-if="isAdmin"
                      class="rounded p-1 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-ruby-11"
                      @click="confirmRemoveItem(item.id)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-3.5 w-3.5"
                      >
                        <path
                          d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Loading state -->
                <div
                  v-if="itemResults[item.id]?.loading"
                  class="py-4 text-center text-sm text-n-slate-11"
                >
                  {{ t('DATA_ASSISTANT.QUERY_RUNNING') }}
                </div>

                <!-- Error state -->
                <div
                  v-else-if="itemResults[item.id]?.error"
                  class="rounded bg-n-ruby-3 p-2 text-xs text-n-ruby-11"
                >
                  {{ t('DATA_ASSISTANT.QUERY_FAILED') }}:
                  {{ itemResults[item.id].error }}
                </div>

                <!-- Result -->
                <ResultVisualization
                  v-else-if="itemResults[item.id]?.result_data"
                  :result-type="item.result_type"
                  :result-data="itemResults[item.id].result_data"
                  :conv-filter-params="item.conv_filter"
                  @segment-click="
                    payload => openConversationFilterHandler(item, payload)
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <div
        v-else
        class="flex flex-1 items-center justify-center text-n-slate-11"
      >
        <p>{{ t('DATA_ASSISTANT.REPORTS_EMPTY_STATE') }}</p>
      </div>
    </div>

    <Dialog
      ref="deleteReportDialogRef"
      type="alert"
      :title="t('DATA_ASSISTANT.DELETE_REPORT_CONFIRM')"
      :description="t('DATA_ASSISTANT.DELETE_REPORT_CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('DATA_ASSISTANT.DELETE_CONFIRM_YES')"
      @confirm="deleteReport"
    />

    <Dialog
      ref="deleteItemDialogRef"
      type="alert"
      :title="t('DATA_ASSISTANT.DELETE_ITEM_CONFIRM')"
      :description="t('DATA_ASSISTANT.DELETE_ITEM_CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('DATA_ASSISTANT.DELETE_CONFIRM_YES')"
      @confirm="removeItem"
    />

    <ReportCollaboratorsDialog
      ref="collaboratorsDialogRef"
      :report-id="currentReport?.id"
    />
  </div>
</template>

<style lang="scss" scoped>
.dropdown-pane {
  .dropdown-menu__item {
    @apply mb-0;
  }
}
</style>
